import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button, Card, CardHeader, CardTitle, Input } from '@monigo/ui';
import { callFunction } from '../../lib/api/client.js';
import { supabase } from '../../lib/supabase.js';

interface SubmissionRow {
  id: string;
  user_id: string;
  id_document_type: string;
  id_document_storage_key: string;
  selfie_storage_key: string;
}

export function KycReviewPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [row, setRow] = useState<SubmissionRow | null>(null);
  const [selfieUrl, setSelfieUrl] = useState<string>('');
  const [idUrl, setIdUrl] = useState<string>('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      const { data } = await supabase
        .from('kyc_submissions')
        .select('id, user_id, id_document_type, id_document_storage_key, selfie_storage_key')
        .eq('id', id)
        .maybeSingle();
      if (!data) return;
      setRow(data);
      const [a, b] = await Promise.all([
        supabase.storage.from('kyc-documents').createSignedUrl(data.selfie_storage_key, 600),
        supabase.storage.from('kyc-documents').createSignedUrl(data.id_document_storage_key, 600),
      ]);
      if (a.data) setSelfieUrl(a.data.signedUrl);
      if (b.data) setIdUrl(b.data.signedUrl);
    })();
  }, [id]);

  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') =>
      callFunction('admin-kyc-decide', {
        body: {
          submission_id: id,
          decision,
          rejection_reason: decision === 'rejected' ? reason : undefined,
        },
      }),
    onSuccess: () => navigate('/kyc'),
    onError: () => setError(t('errors.generic')),
  });

  if (!row) return <Card>{t('common.loading')}</Card>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('kyc.reviewTitle')}</CardTitle>
      </CardHeader>
      <div className="grid gap-4 md:grid-cols-2">
        <ImagePane label={t('kyc.selfieLabel')} url={selfieUrl} />
        <ImagePane label={`${t('kyc.idLabel')} (${row.id_document_type})`} url={idUrl} />
      </div>
      <div className="mt-4 flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{t('kyc.rejectionReason')}</span>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        <div className="flex gap-2">
          <Button onClick={() => decide.mutate('approved')} isLoading={decide.isPending}>
            {t('kyc.approveCta')}
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (!reason.trim()) {
                setError(t('kyc.decisionRequired'));
                return;
              }
              decide.mutate('rejected');
            }}
            isLoading={decide.isPending}
          >
            {t('kyc.rejectCta')}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function ImagePane({ label, url }: { label: string; url: string }) {
  return (
    <div className="rounded-md border border-border bg-surface-elevated p-3">
      <div className="mb-2 text-sm font-semibold">{label}</div>
      {url ? (
        <img src={url} alt={label} className="max-h-96 w-full rounded-md object-contain" />
      ) : (
        <div className="h-48 animate-pulse rounded-md bg-border/40" />
      )}
    </div>
  );
}
